import { useCallback } from "react";
import { getAddress, formatUnits, parseUnits, Contract, parseEther } from 'ethers';

const erc20Abi = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)"
];

function useConfirmTransaction(provider, address, contractAddress) {
  const confirmTransaction = useCallback(
    async (cantidadEth, isValid, bitcoinAddress, onConfirmed, onBlockConfirm, onTransferSent, tokenAddress, tokenDecimals) => {
      if (!provider) {
        alert("Please connect your wallet first.");
        return;
      }

      if (!cantidadEth || cantidadEth <= 0) {
        alert("Please enter a positive amount.");
        return;
      }

      if (!isValid) {
        alert("Please enter a valid Bitcoin Testnet address.");
        return;
      }

      if (!contractAddress) {
        alert("Deposit address not configured for the current network.");
        return;
      }

      try {
        const ethersProvider = provider;
        const checksummedAddress = getAddress(address);
        const signer = await ethersProvider.getSigner();
        const amountStr = cantidadEth.toString();

        if (tokenAddress) {
          const tokenContract = new Contract(tokenAddress, erc20Abi, signer);
          const balanceWei = await tokenContract.balanceOf(checksummedAddress);
          const balance = parseFloat(formatUnits(balanceWei, tokenDecimals));

          if (balance < cantidadEth) {
            alert("Insufficient balance to complete the transaction.");
            return;
          }
          
          const nativeBalanceWei = await ethersProvider.getBalance(checksummedAddress);
          // A simple gas check, can be improved with more accurate estimation
          if (nativeBalanceWei === 0n) {
            alert(`Insufficient native currency to cover gas fees.`);
            return;
          }

          const parsedAmount = parseUnits(amountStr, tokenDecimals);
          
          // Check allowance
          const currentAllowance = await tokenContract.allowance(checksummedAddress, contractAddress);

          if (currentAllowance < parsedAmount) {
            // Approve spending if allowance is not enough
            const approveTx = await tokenContract.approve(contractAddress, parsedAmount);
            await approveTx.wait(); // Wait for approval to be mined
          }

          const txResponse = await tokenContract.transfer(contractAddress, parsedAmount);

          if (typeof onTransferSent === "function") {
            await onTransferSent(txResponse);
          }

          for (let i = 1; i < 2; i++) {
            await txResponse.wait(i);
            if (typeof onBlockConfirm === "function") {
              onBlockConfirm(i); 
            }
          }

          if (typeof onConfirmed === "function") {
            await onConfirmed(txResponse);
          }

        } else { // Native currency logic
          const balanceWei = await provider.getBalance(await signer.getAddress());
          
          const transaction = {
            to: contractAddress,
            value: parseEther(amountStr),
          };

          if (balanceWei < transaction.value) {
            alert(`Insufficient balance to complete the transaction.`);
            return;
          }

          const txResponse = await signer.sendTransaction(transaction);

          if (typeof onTransferSent === "function") {
            await onTransferSent(txResponse);
          }

          for (let i = 1; i < 2; i++) {
            await txResponse.wait(i);
            if (typeof onBlockConfirm === "function") {
              onBlockConfirm(i); 
            }
          }

          if (typeof onConfirmed === "function") {
            await onConfirmed(txResponse);
          }
        }
      } catch (error) {
        // Check if the error is a user rejection (standard EIP-1193 code)
        if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
          // Log the user rejection to the console instead of showing an alert
          console.log("Transaction rejected by user.");
        } else {
          // For all other errors, log the error and show a generic alert
          console.error("Error signing transaction:", error);
          alert(`An error occurred while signing the transaction: ${error.message}`);
        }
      }
    },
    [provider, address, contractAddress]
  );

  return { confirmTransaction };
}

export default useConfirmTransaction;